var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useRef } from 'react';
import { SwapV1 } from 'ui-kit';
import { getIcon } from '../utils/getIcon';
import { getCoinDisplayName } from '../utils/coinDisplayUtils';
import { coinsList, getLatestPrices, getSuiClient } from 'alphafi-sdk';
import { SwapGateway, initializeAlphaFi } from 'sui-alpha-sdk';
import { useBalances } from '../context/BalanceContext';
import { BigNumber } from 'bignumber.js';
import BN from 'bn.js';
import { useCurrentAccount, useSignAndExecuteTransaction } from '@mysten/dapp-kit';
// Tokens to exclude from the swap list
const EXCLUDED_TOKENS = [
    'TURBOS',
    'APT',
    'SLP',
    'CELO',
    'BLUB',
    'FT_BLUEFIN_STSUI_SUI',
    'AlphaFi stSUI-SUI LP',
    'EXBTC',
    'ETHIRD',
];
const SwapModal = ({ isOpen, onClose, onNotification, loading, setLoading, walletConnectFunctionality, isWalletConnected, theme, }) => {
    const [payToken, setPayToken] = useState();
    const [receiveToken, setReceiveToken] = useState();
    const [payAmount, setPayAmount] = useState('');
    const [receiveAmount, setReceiveAmount] = useState('');
    const [slippageValue, setSlippageValue] = useState(0.5);
    const [tokens, setTokens] = useState([]);
    const { walletBalancesMap } = useBalances();
    // Quote-related state variables
    const [quoteLoading, setQuoteLoading] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [slippageAmount, setSlippageAmount] = useState(0);
    const [minTokensReceived, setMinTokensReceived] = useState(0);
    const [sendTokenUSDValue, setSendTokenUSDValue] = useState(0);
    const [receiveTokenUSDValue, setReceiveTokenUSDValue] = useState(0);
    const [tokenRatio, setTokenRatio] = useState(0);
    // Initialize SwapGateway and hooks
    const swapGateway = useRef(new SwapGateway()).current;
    const currentAccount = useCurrentAccount();
    const suiClient = getSuiClient();
    const { mutate: signAndExecuteTransaction } = useSignAndExecuteTransaction();
    const debounceTimeout = useRef(null);
    const getQuoteTimestamp = useRef(0);
    const swapOptions = useRef(undefined);
    // Generic function to format token amounts based on token symbol
    const formatTokenAmount = (amount, tokenSymbol) => {
        if (tokenSymbol.toUpperCase() === 'XBTC' ||
            tokenSymbol.toUpperCase() === 'TBTC' ||
            tokenSymbol.toUpperCase() === 'WBTC' ||
            tokenSymbol.toUpperCase() === 'SUIBTC' ||
            tokenSymbol.toUpperCase() === 'BTC' ||
            tokenSymbol.toUpperCase() === 'LBTC' ||
            tokenSymbol.toUpperCase() === 'EBTC' ||
            tokenSymbol.toUpperCase() === 'XAUM' ||
            tokenSymbol.toUpperCase() === 'WETH') {
            return amount.toFixed(8);
        }
        else {
            return amount.toFixed(4);
        }
    };
    // Initialize AlphaFi SDK
    const init = (address, force) => __awaiter(void 0, void 0, void 0, function* () {
        yield initializeAlphaFi({ address: address }, force);
        setIsInitialized(true);
    });
    // Initialize page when wallet connects
    const initializePage = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        yield init(currentAccount ? currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.address : undefined, false);
    }), [currentAccount]);
    // Get quote function similar to Swap.tsx
    const getQuote = useCallback((payAmount, payToken, receiveToken) => __awaiter(void 0, void 0, void 0, function* () {
        if (!payAmount || parseFloat(payAmount) <= 0 || !payToken || !receiveToken) {
            setReceiveAmount('');
            setSlippageAmount(0);
            setMinTokensReceived(0);
            setSendTokenUSDValue(0);
            setReceiveTokenUSDValue(0);
            setTokenRatio(0);
            return;
        }
        try {
            setQuoteLoading(true);
            // Get coin objects from the SDK
            const payTokenCoin = Object.values(coinsList).find((coin) => coin.type === payToken.tokenId);
            const receiveTokenCoin = Object.values(coinsList).find((coin) => coin.type === receiveToken.tokenId);
            if (!payTokenCoin || !receiveTokenCoin) {
                throw new Error('Token not found in coins list');
            }
            // Calculate swap amount in raw units
            const swapAmount = new BigNumber(parseFloat(payAmount) * Math.pow(10, payTokenCoin.expo)).toString();
            const swapAmountFormatted = swapAmount.split('.')[0];
            const swapOptionsData = {
                pair: {
                    coinA: payTokenCoin,
                    coinB: receiveTokenCoin,
                },
                senderAddress: (currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.address) || '',
                inAmount: new BN(swapAmountFormatted),
                slippage: slippageValue,
            };
            swapOptions.current = swapOptionsData;
            const response = yield swapGateway.getBestQuoteFromAll(swapOptionsData);
            if (response) {
                const receiveTokenAmount = response.estimatedAmountOut.toString();
                const receiveAmountDecimal = +receiveTokenAmount / Math.pow(10, receiveTokenCoin.expo);
                // Format receive amount using the generic function
                const receiveAmountFormatted = formatTokenAmount(receiveAmountDecimal, receiveToken.symbol);
                setReceiveAmount(receiveAmountFormatted);
                setSlippageAmount(parseFloat((response.slippage * 100).toFixed(2)));
                // Set USD values for internal calculations
                const inputUSD = response.inputAmountInUSD || 0;
                const outputUSD = response.estimatedAmountOutInUSD || 0;
                setSendTokenUSDValue(inputUSD);
                setReceiveTokenUSDValue(outputUSD);
                // Calculate token ratio for internal use
                const payTokenPrice = parseFloat(payToken.price || '0');
                const receiveTokenPrice = parseFloat(receiveToken.price || '0');
                const ratio = payTokenPrice > 0 && receiveTokenPrice > 0 ? payTokenPrice / receiveTokenPrice : 0;
                const ratioFormatted = formatTokenAmount(ratio, receiveToken.symbol);
                setTokenRatio(parseFloat(ratioFormatted));
                // Calculate minimum tokens received for validation
                const minReceived = ((1 - slippageValue / 100) * parseFloat(receiveTokenAmount)) /
                    Math.pow(10, receiveTokenCoin.expo);
                const minReceivedFormatted = formatTokenAmount(minReceived, receiveToken.symbol);
                setMinTokensReceived(parseFloat(minReceivedFormatted));
            }
        }
        catch (error) {
            console.error('Error getting quote:', error);
            setReceiveAmount('');
            setSlippageAmount(0);
            setMinTokensReceived(0);
            setSendTokenUSDValue(0);
            setReceiveTokenUSDValue(0);
            setTokenRatio(0);
        }
        finally {
            setQuoteLoading(false);
        }
    }), [currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.address, slippageValue, swapGateway]);
    const formatBalance = (rawBalance, decimals) => {
        const balance = parseFloat(rawBalance) / Math.pow(10, decimals);
        if (balance === 0)
            return '0';
        return balance.toString();
    };
    // Helper function to normalize coin types for balance lookup and format balance
    const getWalletBalance = useCallback((coinType, decimals) => {
        if (!walletBalancesMap)
            return '0';
        // Try exact match first
        let balance = walletBalancesMap.get(coinType);
        if (balance)
            return formatBalance(balance, decimals);
        // For SUI, try both formats
        if (coinType.includes('::sui::SUI')) {
            balance = walletBalancesMap.get('0x2::sui::SUI');
            if (balance)
                return formatBalance(balance, decimals);
        }
        // Try normalized format (0x0000...000002 -> 0x2)
        const normalizedType = coinType.replace(/^0x0+/, '0x');
        balance = walletBalancesMap.get(normalizedType);
        if (balance)
            return formatBalance(balance, decimals);
        return '0';
    }, [walletBalancesMap]);
    // Function to fetch tokens with prices from SDK
    const fetchTokensWithPrices = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            // Get all available tokens from coinsList
            const allTokenNames = Object.keys(coinsList);
            // Create price pairs array for all tokens
            const token_arr = allTokenNames.map((tokenName) => {
                return (tokenName + '/USD');
            });
            // Fetch prices for all tokens
            const token_price_arr = yield getLatestPrices(token_arr, false);
            // Create tokens array with SDK data for all tokens
            const tokensWithPrices = allTokenNames
                .map((tokenName, index) => {
                const coin = coinsList[tokenName];
                if (!coin)
                    return null;
                return {
                    symbol: getCoinDisplayName(coin.name),
                    icon: getIcon(coin.name.toLowerCase(), coin.name) || coin.icon,
                    price: token_price_arr[index] || '0',
                    walletBalance: getWalletBalance(coin.type, coin.expo),
                    tokenId: coin.type,
                };
            })
                .filter(Boolean);
            // Filter out excluded tokens
            const filteredTokens = tokensWithPrices.filter((token) => !EXCLUDED_TOKENS.includes(token.symbol));
            const reorderTokenData = filteredTokens.sort((a, b) => {
                return (parseFloat(b.walletBalance || '0') * parseFloat(b.price || '0') -
                    parseFloat(a.walletBalance || '0') * parseFloat(a.price || '0'));
            });
            setTokens(reorderTokenData);
            return reorderTokenData;
        }
        catch (error) {
            console.error('Error fetching tokens with prices:', error);
        }
    }), [getWalletBalance]);
    // Initialize AlphaFi when wallet connects
    useEffect(() => {
        if ((currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.address) && !isInitialized) {
            initializePage();
        }
    }, [currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.address, isInitialized, initializePage]);
    // Fetch tokens on component mount and when wallet balances change
    useEffect(() => {
        fetchTokensWithPrices();
    }, [fetchTokensWithPrices]);
    // Initialize default tokens for swap when tokens are loaded (only if not already set)
    useEffect(() => {
        if (tokens.length > 1 && !payToken && !receiveToken) {
            setPayToken(tokens[0]);
            setReceiveToken(tokens[1]);
        }
    }, [tokens, payToken, receiveToken]);
    // Update selected tokens' balances when walletBalancesMap changes
    useEffect(() => {
        if (payToken && receiveToken && walletBalancesMap) {
            // Find the updated tokens with fresh balances
            const updatedPayToken = tokens.find((token) => token.tokenId === payToken.tokenId);
            const updatedReceiveToken = tokens.find((token) => token.tokenId === receiveToken.tokenId);
            if (updatedPayToken) {
                setPayToken(updatedPayToken);
            }
            if (updatedReceiveToken) {
                setReceiveToken(updatedReceiveToken);
            }
        }
    }, [walletBalancesMap, tokens, payToken, receiveToken]);
    // Debounced quote fetching when pay amount or tokens change
    useEffect(() => {
        if (!isInitialized || !payToken || !receiveToken)
            return;
        if (payAmount && parseFloat(payAmount) > 0) {
            // Clear the previous timeout if the user is still typing
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
            // Set a new timeout
            debounceTimeout.current = setTimeout(() => {
                if (Date.now() > getQuoteTimestamp.current + 500) {
                    getQuote(payAmount, payToken, receiveToken);
                    getQuoteTimestamp.current = Date.now();
                }
            }, 1000); // Delay of 1000ms
        }
        else {
            setReceiveAmount('');
            setSlippageAmount(0);
            setMinTokensReceived(0);
            setSendTokenUSDValue(0);
            setReceiveTokenUSDValue(0);
            setTokenRatio(0);
        }
        // Cleanup function to clear the timeout when component unmounts or dependencies change
        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, [payAmount, payToken, receiveToken, slippageValue, isInitialized, getQuote]);
    // Reset inputs when modal closes
    useEffect(() => {
        if (!isOpen) {
            setPayAmount('');
            setReceiveAmount('');
            setSlippageAmount(0);
            setMinTokensReceived(0);
            setSendTokenUSDValue(0);
            setReceiveTokenUSDValue(0);
            setTokenRatio(0);
        }
    }, [isOpen]);
    const handlePayTokenSelect = (token) => {
        setPayToken(token);
        setPayAmount('');
        setReceiveAmount('');
        setSlippageAmount(0);
        setMinTokensReceived(0);
        setSendTokenUSDValue(0);
        setReceiveTokenUSDValue(0);
        setTokenRatio(0);
    };
    const handleReceiveTokenSelect = (token) => {
        setReceiveToken(token);
        setPayAmount('');
        setReceiveAmount('');
        setSlippageAmount(0);
        setMinTokensReceived(0);
        setSendTokenUSDValue(0);
        setReceiveTokenUSDValue(0);
        setTokenRatio(0);
    };
    const handlePayAmountChange = (val) => {
        // Clean input value similar to Swap.tsx
        let inputValue = val.replace(/[^0-9.]/g, '');
        inputValue = inputValue.replace(/^0+(?!\.|$)/, '');
        setPayAmount(inputValue);
        // Quote fetching is now handled by useEffect hook with debouncing
    };
    const handleDirectionSwitch = () => {
        if (payToken && receiveToken) {
            setPayToken(receiveToken);
            setReceiveToken(payToken);
            setPayAmount(receiveAmount);
            setReceiveAmount(payAmount);
        }
    };
    const handleSwap = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if (!payToken || !receiveToken || !swapOptions.current)
            return;
        try {
            setLoading(true);
            // Show processing notification
            onNotification({
                title: 'Processing Swap',
                description: `Requesting wallet to approve`,
                spinner: true,
                image: null,
            });
            const txb = yield swapGateway.getTransactionBlock();
            if (txb) {
                // Check SUI balance for gas
                const suiToken = tokens.find((token) => token.symbol === 'SUI');
                const suiBalance = parseFloat((suiToken === null || suiToken === void 0 ? void 0 : suiToken.walletBalance) || '0');
                const gasAmount = Number((_a = txb.getData().gasData.budget) !== null && _a !== void 0 ? _a : 1e8) / 1e9; // Convert to SUI units
                if (gasAmount > suiBalance) {
                    onNotification({
                        title: 'Insufficient SUI for Gas',
                        description: 'Please add more SUI to your wallet to proceed.',
                        spinner: false,
                        image: getIcon('failed'),
                    });
                    setLoading(false);
                    return;
                }
                signAndExecuteTransaction({
                    transaction: txb,
                }, {
                    onSuccess: (result) => __awaiter(void 0, void 0, void 0, function* () {
                        onNotification({
                            title: 'Broadcasting Transaction',
                            description: 'Broadcasting transaction...',
                            spinner: true,
                            image: null,
                        });
                        try {
                            const txbCheck = yield suiClient.waitForTransaction({
                                digest: result.digest,
                                options: {
                                    showEffects: true,
                                },
                            });
                            if (txbCheck.effects &&
                                txbCheck.effects.status &&
                                txbCheck.effects.status.status === 'success') {
                                onNotification({
                                    title: 'Swap Successful',
                                    description: `Successfully swapped ${payAmount} ${getCoinDisplayName(payToken.symbol)} to ${receiveAmount} ${getCoinDisplayName(receiveToken.symbol)}`,
                                    spinner: false,
                                    image: getIcon('success'),
                                    link: `https://suiscan.xyz/mainnet/tx/${result.digest}`,
                                });
                                // Clear input fields and close modal
                                setPayAmount('');
                                setReceiveAmount('');
                                setSlippageAmount(0);
                                setMinTokensReceived(0);
                                setSendTokenUSDValue(0);
                                setReceiveTokenUSDValue(0);
                                setTokenRatio(0);
                                // Re-initialize AlphaFi to refresh data
                                initializeAlphaFi({ address: currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.address }, true);
                                onClose();
                            }
                            else {
                                onNotification({
                                    title: 'Swap Failed',
                                    description: `Your swap of ${payAmount} ${getCoinDisplayName(payToken.symbol)} to ${receiveAmount} ${getCoinDisplayName(receiveToken.symbol)} has failed.`,
                                    spinner: false,
                                    image: getIcon('failed'),
                                    link: `https://suiscan.xyz/mainnet/tx/${result.digest}`,
                                });
                            }
                        }
                        catch (waitError) {
                            console.error('Error waiting for transaction:', waitError);
                            onNotification({
                                title: 'Transaction Status Unknown',
                                description: 'Unable to confirm transaction status. Please check your wallet.',
                                spinner: false,
                                image: getIcon('failed'),
                            });
                        }
                        setLoading(false);
                    }),
                    onError: (err) => __awaiter(void 0, void 0, void 0, function* () {
                        console.error('Transaction error:', err);
                        let title = 'Swap Failed';
                        let description = 'Something went wrong. Please try again later or contact us if the issue persists.';
                        if (err.message === 'User rejected the request.') {
                            title = 'Transaction Rejected';
                            description = `You have rejected the transaction to swap ${payAmount} ${getCoinDisplayName(payToken.symbol)} to ${receiveAmount} ${getCoinDisplayName(receiveToken.symbol)}`;
                        }
                        onNotification({
                            title,
                            description,
                            spinner: false,
                            image: getIcon('failed'),
                        });
                        setLoading(false);
                    }),
                });
            }
        }
        catch (error) {
            console.error('Swap preparation failed:', error);
            onNotification({
                title: 'Swap Failed',
                description: 'Failed to prepare swap transaction. Please try again.',
                spinner: false,
                image: getIcon('failed'),
            });
            setLoading(false);
        }
    });
    const handleRefresh = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            // Set loading state to prevent multiple refresh calls
            setQuoteLoading(true);
            // Refresh token prices first
            yield fetchTokensWithPrices();
            // Refetch quote if there's a valid pay amount and tokens selected
            if (payAmount && parseFloat(payAmount) > 0 && payToken && receiveToken) {
                yield getQuote(payAmount, payToken, receiveToken);
            }
        }
        catch (error) {
            console.error('Error during refresh:', error);
            // Optionally show a notification about refresh failure
            onNotification({
                title: 'Refresh Failed',
                description: 'Failed to refresh prices and quotes. Please try again.',
                spinner: false,
                image: getIcon('failed'),
            });
        }
        finally {
            setQuoteLoading(false);
        }
    });
    if (!payToken || !receiveToken) {
        return null;
    }
    // Modified wallet balance for SUI by maintaining required gas fees
    const adjustedPayToken = payToken.symbol.toLowerCase() === 'sui'
        ? Object.assign(Object.assign({}, payToken), { walletBalance: Math.max(0, parseFloat(payToken.walletBalance || '0') - 0.05).toFixed(6) }) : payToken;
    return (_jsx(SwapV1, { title: "Swap", payAmount: payAmount, swapIcon: theme === 'dark' ? getIcon('whiteSwapIcon') : getIcon('blackSwapIcon'), downArrow: theme === 'dark' ? getIcon('whiteDownArrow') : getIcon('blackDownArrow'), slippageValue: slippageValue, slippagePercentage: slippageAmount, onSlippageChange: setSlippageValue, slippageIcon: theme === 'dark' ? getIcon('whiteSlippageIcon') : getIcon('blackSlippageIcon'), refreshIcon: theme === 'dark' ? getIcon('whiteReloadIcon') : getIcon('blackReloadIcon'), onRefresh: handleRefresh, viceVersaIcon: theme === 'dark' ? getIcon('viceVersaIconWhite') : getIcon('viceVersaIconBlack'), receiveAmount: receiveAmount, onPayAmountChange: handlePayAmountChange, payToken: adjustedPayToken, payBalance: payToken && payToken.symbol.toLowerCase() === 'sui'
            ? Math.max(0, parseFloat(payToken.walletBalance || '0') - 0.05).toString() || '0'
            : payToken.walletBalance || '0', receiveBalance: receiveToken.walletBalance || '0', highlightedTokens: [tokens[0], tokens[1], tokens[2], tokens[3], tokens[4], tokens[5]], closeIcon: theme === 'dark' ? getIcon('whiteCloseIcon') : getIcon('blackCloseIcon'), walletIcon: theme === 'dark' ? getIcon('darkWalletIcon') : getIcon('lightWalletIcon'), receiveToken: receiveToken, onPayTokenSelect: handlePayTokenSelect, onReceiveTokenSelect: handleReceiveTokenSelect, tokens: tokens, isOpen: isOpen, onClose: onClose, onSwap: handleSwap, swapDisabled: !payAmount ||
            parseFloat(payAmount) <= 0 ||
            loading ||
            quoteLoading ||
            !currentAccount ||
            parseFloat(payAmount) > parseFloat(payToken.walletBalance || '0') ||
            parseFloat(receiveAmount) <= 0 ||
            minTokensReceived <= 0 ||
            (slippageAmount < 0 && Math.abs(slippageAmount) > slippageValue) ||
            (sendTokenUSDValue > 0 && receiveTokenUSDValue <= 0) ||
            tokenRatio <= 0, onDirectionSwitch: handleDirectionSwitch, poweredBy: "7k", clearSearchIcon: getIcon('clearSearchIcon'), searchIcon: theme === 'dark' ? getIcon('searchDark') : getIcon('defaultThemeSearchIcon'), backIcon: theme === 'dark' ? getIcon('whiteBackIcon') : getIcon('blackBackIcon'), walletConnectFunctionality: walletConnectFunctionality, isWalletConnected: isWalletConnected, minTokensReceived: minTokensReceived, tokenRatio: tokenRatio, loading: loading, quoteLoading: quoteLoading }));
};
export default SwapModal;
